import { useEffect, useRef, useCallback, useState } from "react";
import { io, Socket } from "socket.io-client";
import { QaDelivery } from "@shared/schema";

type QaUpdateEvent = {
  type:
    | "claimed" | "released" | "completed" | "synced" | "new_delivery"
    | "photos_updated" | "evidence_updated" | "delivery_updated"
    // SPEC-ActiveTask Phase A/B2 — Active Tasks live-update + early-risk events.
    | "active_started" | "active_removed" | "active_early_risk"
    // SPEC-qa-actions-audit-log — surface presence ticks. Live feed only;
    // the DB row is written when the visit ends (presence_off / disconnect).
    | "presence_on" | "presence_off";
  delivery: QaDelivery;
  claimedBy?: string;
  claimedAt?: string;
  // SPEC-ActiveTask B2 — present on "active_early_risk"; drives the toast/badge.
  earlyRisk?: { atRisk: true; ect: string | null; completeAfter: string | null; minutesEarly: number | null };
  // SPEC-qa-actions-audit-log — presence_on / presence_off payload fields.
  deliveryId?: string;
  userId?: string;
  userName?: string;
  surface?: "qa_detail" | "prescreen_detail" | "active_detail";
  startedAt?: string;
  claimId?: string | null;
  durationSeconds?: number;
  endReason?: "navigation" | "disconnect" | "session_expiry";
  timestamp: string;
};

// SPEC-qa-actions-audit-log — narrow surface names emitted by the three
// detail pages. Mirrors PresenceSurface in server/websocket.ts.
export type PresenceSurface = "qa_detail" | "prescreen_detail" | "active_detail";

type ConnectionState = "connecting" | "connected" | "disconnected" | "error";

// SPEC-q-claim (Phase 2) — point-to-point events the timer module emits
// directly to the claim-owner's sockets. They are NOT qa:update broadcasts
// (not subject to the firehose matrix) so they get their own typed handlers
// hung off the socket directly.
export interface QaClaimWarningEvent {
  claimId: string;
  deliveryId: string;
  expiresAt: string;
  msUntilExpiry: number;
}

export interface QaClaimAutoReleasedEvent {
  claimId: string;
  deliveryId: string;
  claimType: "primary" | "secondary";
  forceNavigate: boolean;
}

// SPEC-OverrideNotification §5.1 — shape of the `notification` WS event the
// server sends via broadcastToUser after the override transaction commits.
export interface QaNotificationEvent {
  type: "notification";
  notificationId: string;
  notificationType: string; // 'override' | future types (§7 extensibility)
  deliveryId: string | null;
  payload: Record<string, unknown>;
  createdAt: string;
}

export function useQaWebSocket(
  userId: string | null,
  onQaUpdate: (event: QaUpdateEvent) => void,
  userInfo?: { userName?: string; email?: string },
  // SPEC-q-claim (Phase 2) — optional handlers for the per-claim PTP events.
  // Wired by qa-detail-v3.tsx; left undefined elsewhere (qa-list never holds
  // a claim, so neither event ever fires for it).
  claimHandlers?: {
    onClaimWarning?: (e: QaClaimWarningEvent) => void;
    onClaimAutoReleased?: (e: QaClaimAutoReleasedEvent) => void;
  },
  // SPEC-OverrideNotification §5.1 — optional handler for targeted `notification`
  // events (override notifications and future types). Mounted by
  // OverrideNotificationHandler; left undefined on all other call sites.
  onNotification?: (e: QaNotificationEvent) => void,
) {
  const socketRef = useRef<Socket | null>(null);
  const [connectionState, setConnectionState] = useState<ConnectionState>("disconnected");
  const onQaUpdateRef = useRef(onQaUpdate);
  const onClaimWarningRef = useRef(claimHandlers?.onClaimWarning);
  const onClaimAutoReleasedRef = useRef(claimHandlers?.onClaimAutoReleased);
  const onNotificationRef = useRef(onNotification);
  useEffect(() => {
    onClaimWarningRef.current = claimHandlers?.onClaimWarning;
    onClaimAutoReleasedRef.current = claimHandlers?.onClaimAutoReleased;
  }, [claimHandlers?.onClaimWarning, claimHandlers?.onClaimAutoReleased]);
  useEffect(() => {
    onNotificationRef.current = onNotification;
  }, [onNotification]);
  
  useEffect(() => {
    onQaUpdateRef.current = onQaUpdate;
  }, [onQaUpdate]);

  useEffect(() => {
    if (!userId) return;

    setConnectionState("connecting");

    const socket = io({
      path: "/socket.io",
      transports: ["websocket", "polling"],
      auth: {
        userId,
      },
    });

    socketRef.current = socket;

    socket.on("connect", () => {
      console.log("WebSocket connected");
      setConnectionState("connected");
      socket.emit("qa:auth", { 
        userId, 
        userName: userInfo?.userName || "", 
        email: userInfo?.email || "" 
      });
    });

    socket.on("disconnect", (reason) => {
      console.log("WebSocket disconnected:", reason);
      setConnectionState("disconnected");
    });

    socket.on("connect_error", (error) => {
      console.error("WebSocket connection error:", error);
      setConnectionState("error");
    });

    socket.on("qa:update", (event: QaUpdateEvent) => {
      console.log("QA update received:", event);
      onQaUpdateRef.current(event);
    });

    socket.on("qa:error", (error: { message: string }) => {
      console.error("QA WebSocket error:", error.message);
    });

    // SPEC-OverrideNotification §5.1 — targeted `notification` events from
    // broadcastToUser (not `qa:update` — a separate socket event name).
    socket.on("notification", (e: QaNotificationEvent) => {
      onNotificationRef.current?.(e);
    });

    // SPEC-q-claim (Phase 2) — per-claim PTP events. AC-22 / AC-23.
    socket.on("qa:claim:warning", (e: QaClaimWarningEvent) => {
      onClaimWarningRef.current?.(e);
    });
    socket.on("qa:claim:auto_released", (e: QaClaimAutoReleasedEvent) => {
      onClaimAutoReleasedRef.current?.(e);
    });

    const heartbeatInterval = setInterval(() => {
      if (socket.connected) {
        socket.emit("qa:heartbeat");
      }
    }, 30000);

    return () => {
      clearInterval(heartbeatInterval);
      socket.disconnect();
      socketRef.current = null;
    };
  }, [userId]);

  const claimDelivery = useCallback((taskId: string) => {
    if (socketRef.current?.connected) {
      socketRef.current.emit("qa:claim", { taskId });
    }
  }, []);

  const releaseDelivery = useCallback((taskId: string) => {
    if (socketRef.current?.connected) {
      socketRef.current.emit("qa:release", { taskId });
    }
  }, []);

  const completeDelivery = useCallback((taskId: string, status: "Good" | "Bad") => {
    if (socketRef.current?.connected) {
      socketRef.current.emit("qa:complete", { taskId, status });
    }
  }, []);

  // SPEC-qa-actions-audit-log — presence emitters. Called by the three detail
  // pages on mount/unmount via a useEffect cleanup. The server writes the
  // presence_visit DB row when off (or on disconnect); these emits drive the
  // live feed.
  const emitPresenceOn = useCallback(
    (deliveryId: string, surface: PresenceSurface) => {
      if (socketRef.current?.connected) {
        socketRef.current.emit("presence:on", { delivery_id: deliveryId, surface });
      }
    },
    [],
  );

  const emitPresenceOff = useCallback((deliveryId: string) => {
    if (socketRef.current?.connected) {
      socketRef.current.emit("presence:off", { delivery_id: deliveryId });
    }
  }, []);

  return {
    connectionState,
    claimDelivery,
    releaseDelivery,
    completeDelivery,
    emitPresenceOn,
    emitPresenceOff,
  };
}
